import { useCallback } from "react";
import { Share, Linking } from "react-native";

type SocialShareOptions = {
  refundAmount: string;
  companyName: string;
  shareUrl: string;
};

export const useSocialShare = ({
  refundAmount,
  companyName,
  shareUrl,
}: SocialShareOptions) => {
  const handleSocialShare = useCallback(
    async (platform: string) => {
      const message = `I just got a $${refundAmount} refund from ${companyName} using Sparrow! 🎉`;

      try {
        if (platform === "general") {
          await Share.share({
            message: `${message} ${shareUrl}`,
            url: shareUrl,
          });
        } else {
          // Platform-specific sharing
          let url = "";
          const encodedMessage = encodeURIComponent(message);
          const encodedUrl = encodeURIComponent(shareUrl);

          switch (platform) {
            case "facebook":
              url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
              break;
            case "twitter":
              url = `https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedUrl}`;
              break;
            case "instagram":
              // Instagram doesn't support direct URL sharing, fallback to general share
              await Share.share({
                message: `${message} ${shareUrl}`,
                url: shareUrl,
              });
              return;
            case "messenger":
              url = `https://www.messenger.com/t/?link=${encodedUrl}`;
              break;
          }

          if (url) {
            const supported = await Linking.canOpenURL(url);
            if (supported) {
              await Linking.openURL(url);
            } else {
              // Fallback to general share
              await Share.share({
                message: `${message} ${shareUrl}`,
                url: shareUrl,
              });
            }
          }
        }
      } catch (shareError) {
        console.error("Share error:", shareError);
      }
    },
    [refundAmount, companyName, shareUrl]
  );

  return { handleSocialShare };
};
